import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStorm } from './StormContext';
import { stormParams } from './stormParams';
import { useFpsBackoff } from './useFpsBackoff';
import { pointerEngine } from '../play/pointerEngine';
import { playLoop } from '../play/playLoop';
import { PRESETS, Spring, stepSpring, settled } from '../play/springs';
import './Storm.css';

// Pause the storm when the tab is hidden (perf budget) and under reduced motion.
function usePageVisible(): boolean {
  const [visible, setVisible] = useState(() => !document.hidden);
  useEffect(() => {
    const onChange = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

const Storm: React.FC = () => {
  const { intensity, reducedMotion } = useStorm();
  const p = useMemo(() => stormParams(intensity), [intensity]);
  const visible = usePageVisible();
  const q = useFpsBackoff();
  const animate = !reducedMotion && visible && q < 2;

  // Keep the static video paused when not animating (tab hidden / reduced motion).
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (animate) {
      void v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [animate]);

  // Storm pull: the static layer drifts up to 12px toward the pointer on a lazy spring.
  const pull = !reducedMotion && q < 2;
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !pull) return;
    let sx: Spring = { x: 0, v: 0 };
    let sy: Spring = { x: 0, v: 0 };
    let unreg: (() => void) | null = null;
    const tick = (dt: number) => {
      const cx = window.innerWidth / 2, cy = window.innerHeight / 2;
      const k = 12 / Math.max(cx, cy, 1);
      const tx = (pointerEngine.state.x - cx) * k, ty = (pointerEngine.state.y - cy) * k;
      sx = stepSpring(sx, tx, dt, PRESETS.lazy);
      sy = stepSpring(sy, ty, dt, PRESETS.lazy);
      v.style.transform = `translate3d(${sx.x}px,${sy.x}px,0) scale(1.03)`;
      if (settled(sx, tx) && settled(sy, ty) && unreg) { unreg(); unreg = null; }
    };
    const wake = () => { if (!unreg) unreg = playLoop.register(tick); };
    const off = pointerEngine.on('move', wake);
    return () => {
      off();
      if (unreg) unreg();
      v.style.transform = '';
    };
  }, [pull]);

  return (
    <div className="storm-root" aria-hidden="true" style={{ ['--storm' as any]: intensity }}>
      <video
        ref={videoRef}
        className="storm-static"
        muted
        loop
        playsInline
        preload="auto"
        style={{ opacity: reducedMotion ? 0.12 : p.staticOpacity }}
      >
        <source src="/background/static.webm" type="video/webm" />
      </video>

      {/* particle "stars" removed — the distorted static.webm is the background, per design */}

      {q < 1 && <div className="storm-scanlines" style={{ opacity: reducedMotion ? 0.06 : p.scanlineOpacity }} />}
      <div className="storm-vignette" style={{ opacity: reducedMotion ? 0.2 : p.vignette }} />
    </div>
  );
};

export default Storm;
